import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Header from '@/components/layout/Header';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ShoppingCart, ArrowLeft, Shirt, UtensilsCrossed, Home, Car } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { getImageUrl } from '@/utils/helpers';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CategoryProductsPage = () => {
  const [searchParams] = useSearchParams();
  const categoryType = searchParams.get('type') || 'all'; // clothing, food, living, travel
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { token } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);

  // Category type to category name mapping (includes both old and new names)
  const categoryTypeMap = {
    clothing: ['有机棉服饰', '环保布包', '天然配饰'],
    food: ['新鲜蔬菜', '有机水果', '天然谷物', '健康零食', '豆类制品', '坚果干果', '饮品茶类', '蔬菜', '水果', '谷物', '零食', '豆类', '坚果'],
    living: ['家居用品', '环保清洁', '天然护肤'],
    travel: ['户外装备', '环保出行'],
  };

  // 使用图标替代文字，解决多语言问题
  const categoryTypeInfo = {
    clothing: { 
      titleKey: 'products.categories.clothing',
      icon: Shirt,
      color: 'from-purple-500 to-purple-600',
      descKey: 'products.categories.clothingDesc'
    },
    food: { 
      titleKey: 'products.categories.food',
      icon: UtensilsCrossed,
      color: 'from-green-500 to-green-600',
      descKey: 'products.categories.foodDesc'
    },
    living: { 
      titleKey: 'products.categories.living',
      icon: Home,
      color: 'from-amber-500 to-amber-600',
      descKey: 'products.categories.livingDesc'
    },
    travel: { 
      titleKey: 'products.categories.travel',
      icon: Car,
      color: 'from-blue-500 to-blue-600',
      descKey: 'products.categories.travelDesc'
    },
  };

  useEffect(() => {
    fetchData();
  }, [categoryType]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/categories?type=product`),
      ]);
      
      setCategories(categoriesRes.data);
      
      // Filter products by category type
      let filteredProducts = productsRes.data;
      if (categoryType !== 'all' && categoryTypeMap[categoryType]) {
        const allowedCategories = categoryTypeMap[categoryType];
        filteredProducts = productsRes.data.filter(p => allowedCategories.includes(p.category));
      }
      
      setProducts(filteredProducts);
    } catch (err) {
      console.error('Failed to fetch data', err);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = async (productId) => {
    if (!token) {
      toast.error(t('cart.loginRequired', '请先登录'));
      navigate('/');
      return;
    }
    try {
      await axios.post(`${API}/cart/add`, { product_id: productId, quantity: 1 }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t('cart.addSuccess', '已添加到购物车'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    }
  };

  const getLocalizedField = (item, field) => {
    const lang = i18n.language;
    if (lang === 'en' && item[`${field}_en`]) return item[`${field}_en`];
    if (lang === 'ru' && item[`${field}_ru`]) return item[`${field}_ru`];
    return item[field] || '';
  };

  const currentTypeInfo = categoryTypeInfo[categoryType] || { 
    titleKey: 'products.categories.all', 
    icon: ShoppingCart,
    color: 'from-gray-500 to-gray-600',
    descKey: 'products.categories.allDesc'
  };
  const IconComponent = currentTypeInfo.icon;
  const pageTitle = t(currentTypeInfo.titleKey);
  const pageDesc = t(currentTypeInfo.descKey);

  // Get relevant categories for current type
  const relevantCategories = categoryType !== 'all' && categoryTypeMap[categoryType] 
    ? categories.filter(c => categoryTypeMap[categoryType].includes(c.name))
    : categories;

  // Filter products by selected sub-category
  const displayProducts = selectedCategory 
    ? products.filter(p => p.category === selectedCategory)
    : products;

  const getCategoryDisplayName = (cat) => {
    if (i18n.language === 'en' && cat.name_en) return cat.name_en;
    if (i18n.language === 'ru' && cat.name_ru) return cat.name_ru;
    return cat.name;
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-50 pt-16">
        {/* Hero Section */}
        <div className={`bg-gradient-to-r ${currentTypeInfo.color} text-white py-16`}>
          <div className="max-w-7xl mx-auto px-4">
            <Link to="/lifestyle">
              <Button variant="ghost" className="text-white/80 hover:text-white mb-4">
                <ArrowLeft className="w-4 h-4 mr-2" /> {t('common.back')}
              </Button>
            </Link>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
                <IconComponent className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-4xl font-bold">{pageTitle}</h1>
                {pageDesc && <p className="text-white/80 mt-2">{pageDesc}</p>}
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Sub-category Filter */}
          {relevantCategories.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-8">
              <Button 
                variant={selectedCategory === null ? 'default' : 'outline'} 
                size="sm"
                onClick={() => setSelectedCategory(null)}
              >
                {t('products.categories.all')}
              </Button>
              {relevantCategories.map((cat) => (
                <Button
                  key={cat.id}
                  variant={selectedCategory === cat.name ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedCategory(cat.name)}
                >
                  {cat.icon} {getCategoryDisplayName(cat)}
                </Button>
              ))}
            </div>
          )}

          {/* Products Grid */}
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
            </div>
          ) : displayProducts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-amber-600 text-lg">{t('products.noProducts', '暂无商品')}</p>
              <p className="text-amber-500 mt-2">{t('products.checkBackLater', '请稍后再来查看')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {displayProducts.map((product) => (
                <Card 
                  key={product.id} 
                  className="group overflow-hidden hover:shadow-lg transition-shadow"
                  data-testid={`product-card-${product.id}`}
                >
                  <Link to={`/product/${product.id}`} className="block">
                    <div className="aspect-square relative overflow-hidden bg-amber-100">
                      <img loading="lazy"
                        src={getImageUrl(product.image_url)} 
                        alt={getLocalizedField(product, 'name')} 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      {product.badge && (
                        <Badge className="absolute top-2 left-2 bg-red-500">{product.badge}</Badge>
                      )}
                    </div>
                  </Link>
                  <CardContent className="p-4">
                    <Link to={`/product/${product.id}`}>
                      <h3 className="font-medium text-amber-900 line-clamp-2 mb-2 hover:text-amber-700 transition-colors">
                        {getLocalizedField(product, 'name')}
                      </h3>
                    </Link>
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-lg font-bold text-green-600">¥{product.price.toFixed(2)}</span>
                        {product.original_price && product.original_price > product.price && (
                          <span className="text-sm text-amber-500 line-through ml-2">¥{product.original_price.toFixed(2)}</span>
                        )}
                      </div>
                      <Button 
                        size="icon" 
                        variant="ghost"
                        className="text-green-600 hover:bg-green-50"
                        onClick={() => addToCart(product.id)}
                        data-testid={`add-to-cart-${product.id}`}
                      >
                        <ShoppingCart className="w-5 h-5" />
                      </Button>
                    </div>
                    <p className="text-xs text-amber-600 mt-2">{t(`categories.${product.category}`, product.category)}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CategoryProductsPage;
